import React, { useEffect, useMemo, useState } from 'react';

type TrainingGuide = {
  id: string;
  title: string;
  role: string;
  duration: string;
  page: string;
  purpose: string;
  steps: string[];
  scenes: string[];
  script: string;
};

type TrainingMeta = Record<string, {
  status: string;
  videoLink: string;
  notes: string;
}>;

const STATUS_OPTIONS = ['Not recorded', 'Recording', 'Uploaded', 'Approved'];

const TRAINING_GUIDES: TrainingGuide[] = [
  {
    id: 'remote-start',
    title: 'Remote Workplace Start Guide',
    role: 'All staff',
    duration: '1 min 20 sec',
    page: '/remote-workplace',
    purpose: 'Understand login, tasks, tools, work submission and Mahmood review.',
    steps: [
      'Open the Remote Workplace link.',
      'Enter your staff access code.',
      'Check Assigned Tasks first.',
      'Use the correct tool for your role.',
      'Submit work through Server Submit Work.',
      'Wait for Mahmood review.'
    ],
    scenes: [
      'Welcome to the NexoraX Remote Workplace. This is the secure team hub for staff tasks and work review.',
      'Each team member logs in using their own staff access code.',
      'Start with Assigned Tasks and read the full instructions before doing anything else.',
      'Use the correct tool for your role: Leads, Canva, Smart Quote, Ops Assistant or Submit Work.',
      'When the work is ready, use Server Submit Work and paste the proof, notes or link.',
      'Mahmood reviews the work and marks it approved, needs changes or completed.'
    ],
    script: `Welcome to the NexoraX Remote Workplace.

This is the secure team hub for daily tasks, lead discovery, campaign work, smart quote reviews and work submissions.

Each team member has their own access code.

Start by checking Assigned Tasks. Read the title, priority, due date and full instructions.

Use the correct tools for your role.

When the work is ready, use Server Submit Work. Add the business or campaign name, paste your notes or links, then submit.

Mahmood will review the work and mark it approved, needs changes or completed.`
  },
  {
    id: 'anjum-leads',
    title: 'Anjum Leads Workflow',
    role: 'Anjum — Leads',
    duration: '1 min 20 sec',
    page: '/remote-workplace',
    purpose: 'Research local leads, check business details and submit a lead report.',
    steps: [
      'Check Assigned Tasks.',
      'Use Lead Discovery Engine.',
      'Open Google Search, Maps Search and Facebook Search.',
      'Check website, Facebook, reviews, phone, email and weak points.',
      'Use Offer Matrix to choose best NexoraX offer.',
      'Ask Ops Assistant for summary or outreach idea.',
      'Do not contact businesses without Mahmood approval.',
      'Submit the report to Server Submit Work.'
    ],
    scenes: [
      'Welcome to the Anjum Leads Workflow inside the NexoraX Remote Workplace.',
      'Log in with the Anjum Leads access code and start from Assigned Tasks.',
      'Open the newest task and read the title, priority, due date and instructions.',
      'Use the Lead Discovery Engine. Enter the business type and town, then generate targets.',
      'Open Google Search, Maps Search and Facebook Search to check each business.',
      'Check the business name, website, Facebook page, reviews, phone, email and weak points.',
      'Use the NexoraX Offer Matrix to choose the best service fit.',
      'Ask the Ops Assistant for a short lead summary, offer idea or sample outreach message.',
      'Do not contact any business unless Mahmood approves it first.',
      'When finished, submit notes and findings through Server Submit Work.'
    ],
    script: `Welcome to the Anjum Leads Workflow inside the NexoraX Remote Workplace.

Log in with the Anjum Leads access code and start from Assigned Tasks.

Open the newest task and read the title, priority, due date and instructions carefully.

Use the Lead Discovery Engine. Enter the business type and town, then generate the lead research targets.

Open Google Search, Maps Search and Facebook Search to check each business properly.

For every lead, check the business name, website, Facebook page, reviews, phone number, email and weak points.

Use the NexoraX Offer Matrix to choose the best service.

Ask the Ops Assistant for a short lead summary, offer idea or sample outreach message.

Do not contact any business unless Mahmood approves it first.

When finished, use Server Submit Work. Add the campaign name, paste your notes and wait for Mahmood review.`
  },
  {
    id: 'nazia-canva',
    title: 'Nazia Canva / TikTok Workflow',
    role: 'Nazia — Canva & TikTok',
    duration: '1 min 20 sec',
    page: '/remote-workplace',
    purpose: 'Prepare creative drafts, captions, hashtags and campaign notes.',
    steps: [
      'Check Assigned Tasks.',
      'Open Campaign Briefs.',
      'Open Canva Drafts if design work is needed.',
      'Open TikTok Captions if social wording is needed.',
      'Use Ops Assistant for captions, hashtags and design direction.',
      'Prepare the draft or campaign idea.',
      'Do not post publicly without Mahmood approval.',
      'Submit Canva link or notes to Server Submit Work.'
    ],
    scenes: [
      'Welcome to the Nazia Canva and TikTok Workflow inside the NexoraX Remote Workplace.',
      'Log in with the Nazia Canva access code and start from Assigned Tasks.',
      'Read the campaign title, priority, due date and full instructions.',
      'Open Campaign Briefs to understand the service, client type, offer and audience.',
      'Open Canva Drafts when design work is needed.',
      'Use TikTok Captions for hooks, hashtags and social media wording.',
      'Ask the Ops Assistant for caption ideas, hashtags, design direction or stronger wording.',
      'Prepare the Canva draft, caption, hashtag set or campaign idea.',
      'Do not post anything publicly unless Mahmood approves it first.',
      'Submit the Canva link or notes through Server Submit Work.'
    ],
    script: `Welcome to the Nazia Canva and TikTok Workflow inside the NexoraX Remote Workplace.

Log in with the Nazia Canva access code and start from Assigned Tasks.

Read the campaign title, priority, due date and instructions carefully.

Open Campaign Briefs to understand the service, client type, offer, target audience and main message.

Open Canva Drafts when you need to create or update a design.

Use TikTok Captions for hooks, hashtags and call-to-action ideas.

Ask the Ops Assistant for caption ideas, hashtags, design direction or stronger wording.

Prepare the Canva draft, caption, hashtag set or campaign idea.

Do not post anything publicly unless Mahmood approves it first.

When finished, use Server Submit Work. Add the campaign name, paste the Canva link or notes, and wait for Mahmood review.`
  },
  {
    id: 'submit-work',
    title: 'How to Submit Work',
    role: 'All staff',
    duration: '60 sec',
    page: '/remote-workplace',
    purpose: 'Submit completed work correctly so Mahmood can review it.',
    steps: [
      'Go to Server Submit Work.',
      'Choose the correct work type.',
      'Enter business or campaign name.',
      'Paste notes, lead count, Canva link or findings.',
      'Check the notes are clear.',
      'Click Submit Work to Server.'
    ],
    scenes: [
      'This guide explains how to submit work to the server.',
      'After completing a task, go to Server Submit Work.',
      'Choose the correct work type, such as Lead List, Canva Draft or Client Research.',
      'Enter the business or campaign name.',
      'Paste the work details, notes, lead count, Canva link or findings.',
      'Check the notes are clear, then click Submit Work to Server.'
    ],
    script: `This guide explains how to submit work to the server.

After completing a task, go to Server Submit Work.

Choose the work type, such as Lead List, Canva Draft, TikTok Caption, Campaign Notes or Client Research.

Enter the business or campaign name so Mahmood can identify the work quickly.

Paste the work details. This can include lead notes, lead count, Canva link, captions, campaign ideas or research findings.

Check the notes are clear before submitting.

When ready, click Submit Work to Server.

The work will appear in the Submitted Work Log for Mahmood to review.`
  },
  {
    id: 'ops-assistant',
    title: 'Ops Assistant Guide',
    role: 'All staff',
    duration: '60 sec',
    page: '/remote-workplace',
    purpose: 'Use the assistant for wording, summaries, captions, hashtags and task help.',
    steps: [
      'Open the Ops Assistant.',
      'Ask for lead summaries or outreach ideas.',
      'Ask for captions, hashtags or campaign wording.',
      'Ask for review notes or next steps.',
      'Check the answer before using it.',
      'Submit final work for Mahmood review.'
    ],
    scenes: [
      'This guide explains how to use the NexoraX Ops Assistant.',
      'Use it for lead research, campaign ideas, outreach wording, captions and hashtags.',
      'For lead work, ask for a short summary, offer fit or outreach idea.',
      'For Canva and TikTok, ask for captions, hashtags and design direction.',
      'For admin review, ask it to summarise tasks or prepare next steps.',
      'Always check the answer before using it because Mahmood gives final approval.'
    ],
    script: `This guide explains how to use the NexoraX Ops Assistant.

The Ops Assistant helps with lead research, campaign ideas, outreach wording, captions, hashtags and task summaries.

For lead work, ask it to create a short summary, score an offer fit or prepare a sample outreach idea.

For Canva and TikTok work, ask for caption ideas, hashtags, design direction and call-to-action wording.

For admin review, ask it to summarise tasks or prepare next steps.

Always check the answer before using it.

The assistant helps you work faster, but Mahmood gives final approval.`
  },
  {
    id: 'smart-quote',
    title: 'Smart Quote Workflow',
    role: 'Client to Admin',
    duration: '75 sec',
    page: '/smart-quote',
    purpose: 'Understand how client briefs enter the NexoraX workflow.',
    steps: [
      'Client opens Smart Quote.',
      'Client enters business details.',
      'Client selects services.',
      'Client adds budget, urgency and goals.',
      'System gives recommendation and score.',
      'Client sends brief.',
      'Mahmood reviews it in Remote Workplace.',
      'Mahmood creates Anjum or Nazia tasks.'
    ],
    scenes: [
      'This guide explains the Smart Quote Client Brief Workflow.',
      'The client opens the Smart Quote page.',
      'The client enters business information and chooses services.',
      'The client adds budget, urgency, goals and contact details.',
      'The system creates a recommendation, growth score and next step.',
      'When the client sends the brief, it is saved to the server.',
      'Mahmood reviews it inside Remote Workplace.',
      'Mahmood can create Anjum or Nazia tasks from the brief.'
    ],
    script: `This guide explains the Smart Quote Client Brief Workflow.

The Smart Quote page is for clients who need a website, app, dashboard, AI assistant, QR campaign, SEO, social media, automation or branding.

The client enters business information, chooses services, adds budget, urgency and goals, then enters contact details.

The system creates a NexoraX recommendation with a growth score, conversion score and suggested next step.

When the client sends the brief, it is saved to the server.

Mahmood can review it inside Remote Workplace and create tasks for Anjum or Nazia.`
  },
  {
    id: 'mahmood-admin',
    title: 'Mahmood Admin Review',
    role: 'Mahmood — Admin',
    duration: '75 sec',
    page: '/remote-workplace',
    purpose: 'Review submissions, update statuses, create tasks and monitor activity.',
    steps: [
      'Log in as Mahmood Admin.',
      'Check Remote Mission Control.',
      'Review Incoming Smart Quotes.',
      'Create Anjum or Nazia tasks.',
      'Check Submitted Work Log.',
      'Add Mahmood review notes.',
      'Approve, request changes, archive or delete tests.',
      'Monitor Worker Activity Timeline.'
    ],
    scenes: [
      'This guide explains the Mahmood Admin Review Workflow.',
      'Log in using the Mahmood Admin code.',
      'Start with Remote Mission Control to check tasks, reviews, messages and activity.',
      'Review Incoming Smart Quotes and create Anjum or Nazia tasks when needed.',
      'Check Submitted Work Log when staff submit work.',
      'Add Mahmood review notes and choose the correct status.',
      'Use Archive for old work and Delete Test only for junk test submissions.',
      'Check Worker Activity Timeline to monitor logins, messages, task updates and reviews.'
    ],
    script: `This guide explains the Mahmood Admin Review Workflow.

Log in using the Mahmood Admin code.

Start with Remote Mission Control. This shows open tasks, pending reviews, saved messages and activity events.

Check Incoming Smart Quotes and create tasks for Anjum or Nazia when needed.

When a worker submits work, it appears in the Submitted Work Log.

Read the notes, check any links, and add Mahmood review notes.

Choose the correct status: approved, needs changes, completed, archived or rejected.

Use Delete Test only for junk test submissions.

Finally, check the Worker Activity Timeline to monitor logins, messages, task updates and reviews.`
  }
];

const defaultMeta = {
  status: 'Not recorded',
  videoLink: '',
  notes: ''
};

export default function NexoraXRemoteTrainingHub() {
  const [activeGuide, setActiveGuide] = useState<TrainingGuide>(TRAINING_GUIDES[0]);
  const [notice, setNotice] = useState('');
  const [trainingMeta, setTrainingMeta] = useState<TrainingMeta>({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem('nexorax_remote_training_meta_v1');
      if (saved) {
        setTrainingMeta(JSON.parse(saved));
      }
    } catch {
      setTrainingMeta({});
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('nexorax_remote_training_meta_v1', JSON.stringify(trainingMeta));
    } catch {
      // local storage may be blocked
    }
  }, [trainingMeta]);

  const activeMeta = useMemo(() => ({
    ...defaultMeta,
    ...(trainingMeta[activeGuide.id] || {})
  }), [activeGuide.id, trainingMeta]);

  const updateActiveMeta = (key: keyof typeof defaultMeta, value: string) => {
    setTrainingMeta((prev) => ({
      ...prev,
      [activeGuide.id]: {
        ...defaultMeta,
        ...(prev[activeGuide.id] || {}),
        [key]: value
      }
    }));
  };

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${label} copied.`);
      setTimeout(() => setNotice(''), 2500);
    } catch {
      setNotice('Clipboard blocked. Select and copy manually.');
    }
  };

  const checklistText = activeGuide.steps.map((step, index) => `${index + 1}. ${step}`).join('\n');
  const sceneScriptText = activeGuide.scenes.map((scene, index) => `Scene ${index + 1} — about 8 seconds\n${scene}`).join('\n\n');

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(0,240,255,0.16),transparent_35%),radial-gradient(circle_at_85%_20%,rgba(168,85,247,0.18),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-30 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10">
        <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_55px_rgba(0,240,255,0.12)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-300">
                NexoraX Remote Training Hub
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                Real workflow training from the actual NexoraX system.
              </h1>
              <p className="mt-5 max-w-3xl text-slate-300">
                Use these guide cards, scripts, scene scripts and checklists to train Anjum, Nazia and Mahmood using real NexoraX screens.
              </p>
            </div>

            <div className="grid gap-2">
              <a href="/remote-workplace" className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40">
                Open Remote Workplace
              </a>
              <a href="/guides-videos" className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40">
                Open Guides & Videos
              </a>
              <a href="/smart-quote" className="rounded-xl border border-pink-500/40 bg-pink-950/30 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-900/40">
                Open Smart Quote
              </a>
            </div>
          </div>

          {notice && (
            <div className="mt-5 rounded-2xl border border-cyan-500/30 bg-cyan-950/20 p-4 text-sm text-cyan-100">
              {notice}
            </div>
          )}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.78fr_1.22fr]">
          <section className="space-y-4">
            {TRAINING_GUIDES.map((guide) => {
              const meta = { ...defaultMeta, ...(trainingMeta[guide.id] || {}) };

              return (
                <button
                  key={guide.id}
                  type="button"
                  onClick={() => setActiveGuide(guide)}
                  className={`w-full rounded-3xl border p-5 text-left transition-all ${
                    activeGuide.id === guide.id
                      ? 'border-cyan-400 bg-cyan-950/25 shadow-[0_0_35px_rgba(0,240,255,0.12)]'
                      : 'border-zinc-800 bg-black/60 hover:border-purple-500/40'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">
                      {guide.role} • {guide.duration}
                    </p>
                    <span className={`rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-widest ${
                      meta.status === 'Approved'
                        ? 'border-cyan-400/40 text-cyan-200'
                        : meta.status === 'Uploaded'
                          ? 'border-purple-400/40 text-purple-200'
                          : meta.status === 'Recording'
                            ? 'border-pink-400/40 text-pink-200'
                            : 'border-zinc-700 text-slate-400'
                    }`}>
                      {meta.status}
                    </span>
                  </div>

                  <h3 className="mt-2 text-xl font-black text-white">{guide.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{guide.purpose}</p>
                </button>
              );
            })}
          </section>

          <section className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(168,85,247,0.10)] backdrop-blur-xl">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                  Active Training Guide
                </p>
                <h2 className="mt-2 text-3xl font-black">{activeGuide.title}</h2>
                <p className="mt-2 text-sm text-slate-400">{activeGuide.role} • {activeGuide.duration}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={activeGuide.page}
                  className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                >
                  Open Related Page
                </a>
                <button
                  type="button"
                  onClick={() => copyText(activeGuide.script, 'Full voice script')}
                  className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                >
                  Copy Full Script
                </button>
                <button
                  type="button"
                  onClick={() => copyText(checklistText, 'Checklist')}
                  className="rounded-xl border border-cyan-500/40 bg-black px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
                >
                  Copy Checklist
                </button>
                <button
                  type="button"
                  onClick={() => copyText(sceneScriptText, '8-second scene script')}
                  className="rounded-xl border border-pink-500/40 bg-pink-950/20 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
                >
                  Copy 8-sec Scenes
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-3xl border border-cyan-500/20 bg-cyan-950/10 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                  Step-by-step checklist
                </p>
                <div className="mt-4 space-y-3">
                  {activeGuide.steps.map((step, index) => (
                    <div key={step} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-200">
                      <span className="mr-2 font-mono text-cyan-300">{index + 1}.</span>
                      {step}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-pink-500/20 bg-pink-950/10 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-pink-300">
                  8-second scene script
                </p>
                <div className="mt-4 max-h-[430px] overflow-y-auto space-y-3 pr-2">
                  {activeGuide.scenes.map((scene, index) => (
                    <div key={scene} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-200">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                        Scene {index + 1} • about 8 seconds
                      </p>
                      <p className="mt-2">{scene}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-purple-500/20 bg-purple-950/10 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                  Full voice script
                </p>
                <button
                  type="button"
                  onClick={() => copyText(activeGuide.script, 'Full voice script')}
                  className="rounded-xl border border-purple-500/40 bg-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
                >
                  Copy Script
                </button>
              </div>
              <pre className="mt-4 max-h-[360px] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-zinc-800 bg-black/80 p-5 text-sm leading-relaxed text-slate-200">
{activeGuide.script}
              </pre>
            </div>

            <div className="mt-6 rounded-3xl border border-cyan-500/20 bg-cyan-950/10 p-5">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                Video link and status
              </p>

              <div className="mt-4 grid gap-3 md:grid-cols-[0.7fr_1.3fr]">
                <select
                  value={activeMeta.status}
                  onChange={(e) => updateActiveMeta('status', e.target.value)}
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>

                <input
                  value={activeMeta.videoLink}
                  onChange={(e) => updateActiveMeta('videoLink', e.target.value)}
                  placeholder="Paste final Drive, Canva, MP4 or website video link..."
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                />
              </div>

              <textarea
                value={activeMeta.notes}
                onChange={(e) => updateActiveMeta('notes', e.target.value)}
                placeholder="Training notes, recording notes or changes needed..."
                className="mt-3 min-h-[90px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400"
              />

              <div className="mt-4 flex flex-wrap gap-2">
                {activeMeta.videoLink && (
                  <a
                    href={activeMeta.videoLink}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                  >
                    Open Video Link
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setNotice('Training status and link saved in this browser. Send the final video link later and we can make it permanent on the site.')}
                  className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                >
                  Save Training Info
                </button>
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-pink-500/20 bg-pink-950/10 p-5">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-pink-300">
                Screenshot / clip builder
              </p>
              <p className="mt-3 text-sm text-slate-300">
                Record this guide using your actual NexoraX screen. Use the related page, follow the 8-second scene cards, add the copied script, then paste the final video link above.
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-300">1. Open related page</div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-300">2. Record real workflow</div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-300">3. Upload and paste link</div>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
